import {
  AISuggestionsMatcherAddNewAnythingModalProps,
  AISuggestionsMatcherArrayField,
  AISuggestionsMatcherSingleField,
  AISuggestionsMatcherVariant,
  AISuggestionsMatcherVariantConfig,
  ArrayFieldElement,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { suggestionsToGrantorsSubform } from '@/modules/aiSuggestions/AISuggestionsMatcher/subforms/basicInformation';
import {
  suggestionsToBeneficiariesV2Subform,
  suggestionsToSubtrustLifetimeBeneficiariesSubform,
  suggestionsToSubtrustRemainderBeneficiariesSubform,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/subforms/beneficiaries';
import {
  suggestionsToBeneficialOwnersSubform,
  suggestionsToKeyPeopleSubform,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/subforms/business';
import { suggestionsToDPRecipientsSubform } from '@/modules/aiSuggestions/AISuggestionsMatcher/subforms/dispositiveProvisions';
import {
  suggestionsToSubtrustSuccessorTrusteesSubform,
  suggestionsToSubtrustTrustAdvisorsSubform,
  suggestionsToSubtrustTrusteesSubform,
  suggestionsToSuccessorTrusteesSubform,
  suggestionsToTrustAdvisorsSubform,
  suggestionsToTrusteesSubform,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/subforms/trustDetails';
import { BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE } from '@/modules/beneficiaryDesignations/BeneficiaryDesignationsForm.types';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { getDeathEventPathValue } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';
import { EntityDocumentViewerDocument } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { BASIC_INFORMATION_SUBFORM_NAMESPACE } from '@/modules/entities/BasicInformationSubform/BasicInformationSubform.types';
import { BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
  LIFETIME_BENEFICIARIES_SUBFORM,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { defaultBeneficiary } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.defaults';
import {
  emptySuccessorTrusteeValue,
  emptyTrustAdvisorValue,
  emptyTrusteeValue,
  TRUST_DETAILS_SUBFORM_NAMESPACE,
} from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { AiSuggestionKind } from '@/types/schema';

export function getConfigsForAISuggestionsMatcherVariant(
  entityType: EntityType | null | undefined,
  variant: AISuggestionsMatcherVariant
): AISuggestionsMatcherVariantConfig {
  const commonGrantorConfig: AISuggestionsMatcherVariantConfig = {
    nounSingular: 'grantor',
    nounPlural: 'grantors',
    suggestionKinds: [AiSuggestionKind.Principal],
    addNewAnythingModalProps: {
      [AiSuggestionKind.Principal]: {
        addableTypes: ['individual'],
        addClientProfileModalProps: {
          forceGrantorIfNoClientProfileId: true,
          ignoredQueryDocuments: [EntityDocumentViewerDocument],
        },
        addClientOrganizationModalProps: {},
        addEntityModalProps: {},
      },
    },
    subformArrayFieldUpdateProps: [],
    subformArrayFieldOnConflict: 'overwrite',
  };

  switch (variant) {
    case AISuggestionsMatcherVariant.GRANTORS:
      return {
        ...commonGrantorConfig,
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantors` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToGrantorsSubform,
            idField: 'clientProfileId',
            duplicateGroupLabel: 'Resolve duplicate grantors',
          },
        ],
      };
    case AISuggestionsMatcherVariant.SINGLE_GRANTOR:
      return {
        ...commonGrantorConfig,
        calloutText:
          entityType === 'slat'
            ? 'Only one grantor can be specified for a SLAT.'
            : undefined,
        subformSingleFieldUpdateProps: {
          singleFieldFullPath:
            `${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantor` as const satisfies AISuggestionsMatcherSingleField,
          mapperFunc: (s) => suggestionsToGrantorsSubform(s)?.[0] || '',
        },
      };
    case AISuggestionsMatcherVariant.TRUSTEES:
      return {
        nounSingular: 'trustee',
        nounPlural: 'trustees',
        suggestionKinds: [
          AiSuggestionKind.Trustee,
          AiSuggestionKind.SuccessorTrustee,
          AiSuggestionKind.TrustAdvisor,
        ],
        addNewAnythingModalProps: {
          [AiSuggestionKind.Trustee]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
          [AiSuggestionKind.SuccessorTrustee]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
          [AiSuggestionKind.TrustAdvisor]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToTrusteesSubform,
            idField: 'trusteeId',
            duplicateGroupLabel: 'Resolve duplicate trustees',
          },
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.successorTrustees` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSuccessorTrusteesSubform,
            idField: 'successorTrusteeId',
            duplicateGroupLabel: 'Resolve duplicate successor trustees',
          },
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustAdvisors` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToTrustAdvisorsSubform,
            idField: 'trustAdvisorId',
            duplicateGroupLabel: 'Resolve duplicate trust advisors',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.BENEFICIARIES_V2:
      return {
        nounSingular: 'individual',
        nounPlural: 'individuals',
        suggestionKinds: [AiSuggestionKind.BeneficiaryV2],
        addNewAnythingModalProps: {
          [AiSuggestionKind.BeneficiaryV2]: {
            addableTypes: ['individual', 'organization', 'entity'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              getBeneficiarySubformArrayFieldFullPath(entityType),
            mapperFunc: suggestionsToBeneficiariesV2Subform,
            idField: 'beneficiaryId',
            duplicateGroupLabel: 'Resolve duplicate individuals',
            omitPlaceholder: true,
            insertionOrder: 'prepend',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.BUSINESS_PEOPLE:
      return {
        nounSingular: 'beneficial owner and key person',
        nounPlural: 'beneficial owners and key people',
        suggestionKinds: [
          AiSuggestionKind.BeneficialOwner,
          AiSuggestionKind.KeyPerson,
        ],
        addNewAnythingModalProps: {
          [AiSuggestionKind.BeneficialOwner]: {
            addableTypes: ['individual', 'entity'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {},
            addEntityModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
          },
          [AiSuggestionKind.KeyPerson]: {
            addableTypes: ['individual', 'organization', 'entity'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToBeneficialOwnersSubform,
            idField: 'ownerId',
            suggestionKinds: [AiSuggestionKind.BeneficialOwner],
            // New businesses default to splitting ownership equally between
            // owners as a starting point. Those placeholder rows share the same
            // ownerIds as the AI suggestions, so appending would flag every
            // owner as a duplicate. Overwrite instead so suggested ownership
            // percentages replace the equal-split defaults without a dedupe step.
            onConflict: 'overwrite',
          },
          {
            arrayFieldFullPath:
              `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToKeyPeopleSubform,
            idField: 'keyPersonId',
            duplicateGroupLabel: 'Resolve duplicate key people',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_1DiesFirst
      );
    }
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_2DiesFirst
      );
    }
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_1DiesFirst,
        PrimaryClientDeathEvent.FIRST_DEATH
      );
    }
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_1DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_2DiesFirst,
        PrimaryClientDeathEvent.FIRST_DEATH
      );
    }
    case AISuggestionsMatcherVariant.DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.DpRecipientGrantor_2DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst,
        PrimaryClientDeathEvent.FIRST_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst,
        PrimaryClientDeathEvent.FIRST_DEATH
      );
    }
    case AISuggestionsMatcherVariant.TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH: {
      return getDPVariantConfigs(
        AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst,
        PrimaryClientDeathEvent.SECOND_DEATH
      );
    }
    case AISuggestionsMatcherVariant.SUBTRUST_LIFETIME_BENEFICIARIES:
      return {
        nounSingular: 'individual',
        nounPlural: 'individuals',
        suggestionKinds: [AiSuggestionKind.SubtrustLifetimeBeneficiary],
        addNewAnythingModalProps: {
          [AiSuggestionKind.SubtrustLifetimeBeneficiary]: {
            addableTypes: ['individual', 'organization', 'entity'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${BENEFICIARIES_SUBFORM}.beneficiaries` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSubtrustLifetimeBeneficiariesSubform,
            idField: 'beneficiaryId',
            duplicateGroupLabel: 'Resolve duplicate individuals',
            omitPlaceholder: true,
            insertionOrder: 'prepend',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.SUBTRUST_REMAINDER_BENEFICIARIES:
      return {
        nounSingular: 'individual',
        nounPlural: 'individuals',
        suggestionKinds: [AiSuggestionKind.SubtrustRemainderBeneficiary],
        addNewAnythingModalProps: {
          [AiSuggestionKind.SubtrustRemainderBeneficiary]: {
            addableTypes: ['individual', 'organization', 'entity'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE}.beneficiaries` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSubtrustRemainderBeneficiariesSubform,
            idField: 'beneficiaryId',
            duplicateGroupLabel: 'Resolve duplicate individuals',
            omitPlaceholder: true,
            insertionOrder: 'prepend',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.SUBTRUST_TRUSTEES:
      return {
        nounSingular: 'trustee',
        nounPlural: 'trustees',
        suggestionKinds: [
          AiSuggestionKind.SubtrustTrustee,
          AiSuggestionKind.SubtrustSuccessorTrustee,
          AiSuggestionKind.SubtrustTrustAdvisor,
        ],
        addNewAnythingModalProps: {
          [AiSuggestionKind.SubtrustTrustee]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
          [AiSuggestionKind.SubtrustSuccessorTrustee]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
          [AiSuggestionKind.SubtrustTrustAdvisor]: {
            addableTypes: ['individual', 'organization'],
            addClientProfileModalProps: {
              forceGrantorIfNoClientProfileId: false,
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addClientOrganizationModalProps: {
              ignoredQueryDocuments: [EntityDocumentViewerDocument],
            },
            addEntityModalProps: {},
          },
        },
        subformArrayFieldUpdateProps: [
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSubtrustTrusteesSubform,
            idField: 'trusteeId',
            duplicateGroupLabel: 'Resolve duplicate trustees',
          },
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.successorTrustees` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSubtrustSuccessorTrusteesSubform,
            idField: 'successorTrusteeId',
            duplicateGroupLabel: 'Resolve duplicate successor trustees',
          },
          {
            arrayFieldFullPath:
              `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustAdvisors` as const satisfies AISuggestionsMatcherArrayField,
            mapperFunc: suggestionsToSubtrustTrustAdvisorsSubform,
            idField: 'trustAdvisorId',
            duplicateGroupLabel: 'Resolve duplicate trust advisors',
          },
        ],
        subformArrayFieldOnConflict: 'append',
      };
    case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY:
      return getBeneficiaryDesignationVariantConfigs('primary');
    case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT:
      return getBeneficiaryDesignationVariantConfigs('contingent');
    default:
      throw new Error(`Unknown matcher configs for variant: ${variant}`);
  }
}

/**
 * Beneficiary-designation recipient suggestions reuse the DP_RECIPIENT_V2
 * suggestion shape and the shared DP recipient mapper, but commit into the
 * beneficiary-designations editor's per-tier recipient field array
 * (`beneficiaryDesignations.{primary,contingent}.recipients`) instead of a DP
 * disposition scenario. The tier is decided upstream by the suggestion's
 * `dpBeneficiaryDesignationTier`, which routes it to the primary or contingent
 * matcher; the resulting recipients are persisted by the BDG mutation on save.
 */
function getBeneficiaryDesignationVariantConfigs(
  tier: 'primary' | 'contingent'
): AISuggestionsMatcherVariantConfig {
  const arrayFieldFullPath =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients` as const satisfies AISuggestionsMatcherArrayField;

  const addNewAnythingModalProps: AISuggestionsMatcherAddNewAnythingModalProps =
    {
      addableTypes: ['individual', 'organization', 'entity'],
      addClientProfileModalProps: {
        forceGrantorIfNoClientProfileId: false,
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
      addClientOrganizationModalProps: {
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
      addEntityModalProps: {
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
    };

  return {
    nounSingular: 'recipient',
    nounPlural: 'recipients',
    suggestionKinds: [AiSuggestionKind.DpRecipientV2],
    addNewAnythingModalProps: {
      [AiSuggestionKind.DpRecipientV2]: addNewAnythingModalProps,
    },
    subformArrayFieldUpdateProps: [
      {
        arrayFieldFullPath,
        mapperFunc: suggestionsToDPRecipientsSubform,
        idField: 'recipientId',
        allowDuplicateIds: true,
        sortByNumericField: '_suggestedDispositionOrder',
      },
    ],
    subformArrayFieldOnConflict: 'append',
  };
}

function getBeneficiarySubformArrayFieldFullPath(
  entityType: EntityType | null | undefined
): AISuggestionsMatcherArrayField {
  if (!entityType) {
    throw new Error('Entity type is required');
  }

  switch (entityType) {
    case 'slat':
      return `${LIFETIME_BENEFICIARIES_SUBFORM}.lifetimeBeneficiaries` as const satisfies AISuggestionsMatcherArrayField;
    default:
      // Add more cases as we support more entity types that have custom beneficiary subform fields
      return `${BENEFICIARIES_SUBFORM}.beneficiaries` as const satisfies AISuggestionsMatcherArrayField;
  }
}

function getDPVariantConfigs(
  kind:
    | AiSuggestionKind.DpRecipientGrantor_1DiesFirst
    | AiSuggestionKind.DpRecipientGrantor_2DiesFirst
    | AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst
    | AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst,
  deathEvent?: PrimaryClientDeathEvent | null | undefined
): AISuggestionsMatcherVariantConfig {
  const arrayFieldFullPath = !deathEvent
    ? (`${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies AISuggestionsMatcherArrayField)
    : (`${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.${getDeathEventPathValue(deathEvent)}.recipients` as const satisfies AISuggestionsMatcherArrayField);

  const addNewAnythingModalProps: AISuggestionsMatcherAddNewAnythingModalProps =
    {
      addableTypes: [
        'individual',
        'organization',
        'entity',
        'testamentaryEntity',
      ],
      addClientProfileModalProps: {
        forceGrantorIfNoClientProfileId: false,
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
      addClientOrganizationModalProps: {
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
      addEntityModalProps: {
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
      },
      addTestamentaryEntityModalProps: {
        ignoredQueryDocuments: [EntityDocumentViewerDocument],
        deadClientIds: [],
        dyingPrimaryClientId: null,
      },
    };

  return {
    nounSingular: 'recipient',
    nounPlural: 'recipients',
    suggestionKinds: [kind, AiSuggestionKind.DpRecipientV2],
    addNewAnythingModalProps: {
      [kind]: addNewAnythingModalProps,
      [AiSuggestionKind.DpRecipientV2]: addNewAnythingModalProps,
    },
    subformArrayFieldUpdateProps: [
      {
        arrayFieldFullPath: arrayFieldFullPath,
        mapperFunc: suggestionsToDPRecipientsSubform,
        idField: 'recipientId',
        allowDuplicateIds: true,
        sortByNumericField: '_suggestedDispositionOrder',
      },
    ],
    subformArrayFieldOnConflict: 'append',
    showCommitButtonOnEmptySuggestions: true,
  };
}

/**
 * Mapping of array field form paths to a function that returns a default value
 * for that field. This only includes fields that might have duplicates.
 */
export const ARRAY_FIELD_DEFAULT_VALUE_FN: Partial<
  Record<AISuggestionsMatcherArrayField, () => ArrayFieldElement>
> = {
  [`${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees`]: () => ({
    ...emptyTrusteeValue,
  }),
  [`${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantors`]: () => ({
    clientProfileId: '',
    percentage: null,
  }),
  [`${TRUST_DETAILS_SUBFORM_NAMESPACE}.successorTrustees`]: () => ({
    ...emptySuccessorTrusteeValue,
  }),
  [`${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustAdvisors`]: () => ({
    ...emptyTrustAdvisorValue,
  }),
  [`${BENEFICIARIES_SUBFORM}.beneficiaries`]: defaultBeneficiary,
  [`${LIFETIME_BENEFICIARIES_SUBFORM}.lifetimeBeneficiaries`]:
    defaultBeneficiary,
  [`${BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE}.beneficiaries`]:
    defaultBeneficiary,
};
